import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { Alert, Box, Button, CircularProgress, Grid, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { DataGrid, GridEventListener, GridPaginationModel, GridRowId, GridSortModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { filter, map } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BooleanParam, StringParam, useQueryParam, useQueryParams } from 'use-query-params';

import {
  ActionFormDirtyProvider,
  createActionFormCloseHandler,
  useActionFormDirtyStore,
} from 'components/ActionDialog/actionFormDirtyState';
import { useDataGridStyle } from 'components/atoms/BaseDataGrid/Style';
import { useStudyDashboardStyles } from 'components/atoms/FlexContainers/FlexContainers';
import { RowSelectionContextProvider, useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { getCheckboxColumn } from 'components/atoms/RowSelectionContext/checkboxColumn';
import JobModal from 'components/JobModal';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { StudyDashboardTabTableLayout } from 'components/StudyDashboard/StudyDashboardTabTableLayout';
import { getJobManifestSlideIds, isSecondaryAnalysisJob, Job, JobArchiveState, JobType } from 'interfaces/job';
import { useSnackbar } from 'notistack';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { defaultRowsPerPage, useJobs } from 'utils/queryHooks/jobs/useJobs';
import { useJobsFilteredIds } from 'utils/queryHooks/useJobsFilteredIds';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useJobColumns } from './columns';
import Filters from './Filters';
import { EmbeddedJobDetailsPanel } from './JobDrawer';
import { useJobDrawerWidth } from './jobDrawerSizing';
import { useJobsBatchRebuild } from './useJobsBatchRebuild';

const getJobModalCasesParams = (job: Job, labId: string) => {
  return {
    labId,
    studyId: job.studyId,
    filters: { studyId: job.studyId },
    slideIdsToInclude: getJobManifestSlideIds(job),
    slidesMode: true,
  };
};

type CurrentJobModalQueryParam =
  | {
      jobType: JobType;
      jobData: Job;
      additionalParams?: Record<string, any>;
    }
  | undefined;

const JobsContent: React.FC = () => {
  const theme = useTheme();
  const [currentJobModal, setCurrentJobModal] = useQueryParam<CurrentJobModalQueryParam>(
    'currentJobModal',
    CompressedJsonParam
  );
  const [archivedParam, setArchivedParam] = useQueryParam('archived', BooleanParam);
  const showArchivedJobs = Boolean(archivedParam);
  const archiveState = showArchivedJobs ? JobArchiveState.Archived : JobArchiveState.Active;
  const jobsLabel = showArchivedJobs ? 'archived jobs' : 'jobs';
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [page, setPage] = useState(0);
  const { clearSelection, selectedRows, omittedRows, selectionMode, selectedRowsCount, isRowSelected } =
    useRowSelectionContext();
  const clearSelectionRef = useRef(clearSelection);
  clearSelectionRef.current = clearSelection;

  const { labId } = useCurrentLabId();
  const { jobDrawerWidth } = useJobDrawerWidth();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { mainMarginBottom } = useStudyDashboardStyles();
  const dataGridSx = useDataGridStyle();
  const [isFilterRefreshPending, setIsFilterRefreshPending] = useState(false);
  const [hasObservedFilterRefresh, setHasObservedFilterRefresh] = useState(false);

  const { data, isLoading, isFetching, isError, refetch, jobParams } = useJobs({
    page: page + 1,
    pageSize: defaultRowsPerPage,
    fetchWithInterval: true,
    keepPreviousData: false,
    noInitialData: true,
    orderBy: JSON.stringify(sortModel),
    additionalFilters: { archiveState },
  });
  const isSecondaryAnalysisBulkMode =
    !showArchivedJobs && jobParams.filters.secondaryAnalysisFilter === 'secondary_latest_attempt_per_slide';

  useEffect(() => {
    if (!isSecondaryAnalysisBulkMode) {
      clearSelectionRef.current();
    }
  }, [isSecondaryAnalysisBulkMode]);

  useEffect(() => {
    if (!isFilterRefreshPending) {
      return;
    }

    if (isFetching) {
      setHasObservedFilterRefresh(true);
      return;
    }

    if (hasObservedFilterRefresh) {
      setIsFilterRefreshPending(false);
      setHasObservedFilterRefresh(false);
    }
  }, [hasObservedFilterRefresh, isFetching, isFilterRefreshPending]);

  const handleFiltersSubmitted = () => {
    setIsFilterRefreshPending(true);
    setHasObservedFilterRefresh(false);
  };

  const isRefreshingFilters = isFilterRefreshPending;

  const rowCount = data?.totalJobs ?? data?.jobs?.length;

  const [queryParams, setQueryParams] = useQueryParams({
    jobDrawerOpen: BooleanParam,
    currentJobId: StringParam,
  });
  const isJobDrawerOpen = Boolean(queryParams.jobDrawerOpen && queryParams.currentJobId);
  const activeJobId = isJobDrawerOpen ? queryParams.currentJobId : undefined;

  // Only the first load of an opened job may switch views; archiving an open job must not.
  const normalizedJobIdRef = useRef<string>();

  useEffect(() => {
    if (!activeJobId) {
      normalizedJobIdRef.current = undefined;
    }
  }, [activeJobId]);

  const handleRowClick: GridEventListener<'rowClick'> = (params, event) => {
    const eventTarget = event.target as HTMLElement;
    if (eventTarget?.closest('.MuiDataGrid-cellCheckbox')) {
      return;
    }

    setQueryParams({ jobDrawerOpen: true });
    setQueryParams({ currentJobId: params.row.id });
  };

  const isJobSelectable = (job: Job) => {
    return job.type === JobType.PostProcessingAndCalculateFeatures && isSecondaryAnalysisJob(job);
  };

  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    setPage(paginationModel.page);
  };

  const handleArchiveViewChange = (_event: React.SyntheticEvent, nextShowArchivedJobs: boolean) => {
    setPage(0);
    clearSelection();
    // An open archived job would otherwise force the view back to Archived via handleJobLoaded.
    setQueryParams({ jobDrawerOpen: false, currentJobId: undefined });
    setArchivedParam(nextShowArchivedJobs || undefined, 'replaceIn');
  };

  const handleJobLoaded = useCallback(
    (job: Job) => {
      if (normalizedJobIdRef.current === job.id) {
        return;
      }
      normalizedJobIdRef.current = job.id;
      if (job.deletedAt && !showArchivedJobs) {
        setPage(0);
        clearSelectionRef.current();
        setArchivedParam(true, 'replaceIn');
      }
    },
    [setArchivedParam, showArchivedJobs]
  );

  const currentPageRowIds = map(data?.jobs, 'id').filter((rowId): rowId is string => rowId !== undefined);
  const { jobColumns, isLoading: isLoadingColumnsQueries } = useJobColumns({
    archiveState,
    setCurrentJobModal,
    usersById: data?.usersById,
  });
  const rowSelectionModel = filter(currentPageRowIds, isRowSelected) as GridRowId[];
  const canBatchRebuildJobs = isSecondaryAnalysisBulkMode && selectedRowsCount(rowCount ?? 0) > 0;
  const shouldResolveFilteredJobIds = selectionMode === 'omit' && canBatchRebuildJobs;
  const confirmWithModal = useConfirmation();
  const { contextValue: dirtyStateContextValue, getDirtyState } = useActionFormDirtyStore();

  const filteredJobIdsQuery = useJobsFilteredIds({
    params: {
      filters: jobParams.filters,
      labId: jobParams.labId,
    },
    enabled: shouldResolveFilteredJobIds,
  });

  const {
    isPreparingBatchRebuild,
    isBatchRebuilding,
    isBatchCancellationRequested,
    batchProgressText,
    handleBatchRebuild,
    handleCancelBatchRebuild,
  } = useJobsBatchRebuild({
    canBatchRebuildJobs,
    selectionMode,
    selectedRows,
    omittedRows,
    clearSelection,
    filteredJobIdsQuery,
    confirmWithModal,
    enqueueSnackbar,
    queryClient,
  });

  const columns = useMemo(
    () =>
      isSecondaryAnalysisBulkMode
        ? [getCheckboxColumn(rowCount ?? 0, isBatchRebuilding, isJobSelectable), ...jobColumns]
        : jobColumns,
    [isSecondaryAnalysisBulkMode, rowCount, isBatchRebuilding, jobColumns]
  );

  const onCloseModal = createActionFormCloseHandler(getDirtyState, confirmWithModal, () =>
    setCurrentJobModal(undefined)
  );

  const toolbarContent = (
    <Grid container direction="column" spacing={2} wrap="nowrap">
      <Grid item>
        <Tabs value={showArchivedJobs} onChange={handleArchiveViewChange} aria-label="Job archive view">
          <Tab label="Jobs" value={false} />
          <Tab label="Archived" value={true} />
        </Tabs>
      </Grid>
      <Grid item>
        <Filters sx={{ my: 0 }} isRefreshingFilters={isRefreshingFilters} onFiltersSubmitted={handleFiltersSubmitted} />
      </Grid>
      {isSecondaryAnalysisBulkMode && (
        <Grid item>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="small"
              startIcon={<RestartAltRoundedIcon />}
              onClick={handleBatchRebuild}
              disabled={!canBatchRebuildJobs || isPreparingBatchRebuild || isBatchRebuilding}
            >
              {isPreparingBatchRebuild ? 'Preparing...' : isBatchRebuilding ? 'Rebuilding...' : 'Rebuild Selected'}
            </Button>
            {(isPreparingBatchRebuild || isBatchRebuilding) && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={handleCancelBatchRebuild}
                disabled={isBatchCancellationRequested}
              >
                {isBatchCancellationRequested ? 'Cancellation Requested' : 'Cancel Batch'}
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              Bulk rebuild is available for Secondary Analysis jobs, which otherwise run one case at a time.
            </Typography>
          </Stack>
        </Grid>
      )}
      {(isPreparingBatchRebuild || isBatchRebuilding || Boolean(batchProgressText)) && (
        <Grid item>
          <Alert severity="info">{batchProgressText}</Alert>
        </Grid>
      )}
    </Grid>
  );

  const dataGrid = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        border: `1px solid ${theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {isLoading || isLoadingColumnsQueries ? (
        <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
          <CircularProgress aria-label={`Loading ${jobsLabel}`} />
          <Typography color="text.secondary">Loading {jobsLabel}...</Typography>
        </Stack>
      ) : isError ? (
        <Stack height="100%" alignItems="center" justifyContent="center" p={2}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          >
            Unable to load {jobsLabel}.
          </Alert>
        </Stack>
      ) : (
        <DataGrid
          aria-label={showArchivedJobs ? 'Archived jobs' : 'Jobs'}
          autoHeight={false}
          paginationModel={{ page, pageSize: defaultRowsPerPage }}
          pageSizeOptions={[defaultRowsPerPage]}
          onPaginationModelChange={handlePaginationModelChange}
          loading={isFilterRefreshPending && hasObservedFilterRefresh}
          pagination
          rows={data?.jobs}
          rowCount={rowCount}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={(params) => (params.id === activeJobId ? 'job-row-active' : '')}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          checkboxSelection={isSecondaryAnalysisBulkMode}
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          rowSelection={false}
          rowSelectionModel={rowSelectionModel}
          localeText={{ noRowsLabel: `No ${jobsLabel} found.` }}
          sx={{
            ...dataGridSx,
            height: '100%',
            border: 0,
            borderRadius: 0,
            '& .MuiDataGrid-row.job-row-active, & .MuiDataGrid-row.job-row-active:hover': {
              backgroundColor: theme.palette.action.selected,
            },
            '& .MuiDataGrid-row.job-row-active > .MuiDataGrid-cell, & .MuiDataGrid-row.job-row-active:hover > .MuiDataGrid-cell':
              {
                backgroundColor: 'inherit',
              },
            '& .MuiDataGrid-columnHeaders': {
              borderTop: 0,
            },
          }}
        />
      )}
    </Box>
  );

  const jobModal = currentJobModal?.jobData ? (
    <ActionFormDirtyProvider value={dirtyStateContextValue}>
      <JobModal
        casesParams={getJobModalCasesParams(currentJobModal.jobData, labId)}
        jobType={currentJobModal.jobType}
        jobId={currentJobModal.jobData.id}
        additionalParams={currentJobModal.additionalParams}
        onClose={onCloseModal}
      />
    </ActionFormDirtyProvider>
  ) : null;

  const sidePanel =
    isJobDrawerOpen && queryParams.currentJobId ? (
      <EmbeddedJobDetailsPanel
        currentJobId={queryParams.currentJobId}
        resizable
        onJobLoaded={handleJobLoaded}
        onClose={() => setQueryParams({ jobDrawerOpen: false, currentJobId: undefined })}
      />
    ) : null;

  return (
    <>
      <StudyDashboardTabTableLayout
        marginBottom={mainMarginBottom}
        toolbar={toolbarContent}
        sidePanel={sidePanel}
        sidePanelWidth={jobDrawerWidth}
      >
        {dataGrid}
      </StudyDashboardTabTableLayout>
      {jobModal}
    </>
  );
};

export const EmbeddedJobsView: React.FC = () => {
  return (
    <RowSelectionContextProvider>
      <JobsContent />
    </RowSelectionContextProvider>
  );
};
